import FormsAPI, { FormsFilterType } from 'api/FormsAPI';
import { AxiosResponse } from 'axios';
import { queryClient } from 'config/queryClient';
import { useMemo } from 'react';
import { useQuery } from 'react-query';
import { ConfigType, FORMS_CONFIG_CATEGORY_TYPES } from 'config/config';
import _ from 'lodash';
import { JAVA_SERVICE_ENDPOINTS } from '@buildwise/libs/services';
import { formsKeys } from './query-keys';
import { FormType } from '../types';
import { ArticleUserMapType } from '@buildwise/pages/article/hooks/useArticleDetailsQuery';
import { ArticleType } from '@buildwise/pages/knowledge/hooks/useArticlesQuery';

export type ConfigMappedFormType = ConfigType & {
  status: string;
  articleIds?: string[];
};

export const useFormsQuery = ({
  companyId,
  filters = [],
  shouldFetch = true,
}: {
  companyId: string;
  filters?: FormsFilterType[];
  shouldFetch?: boolean;
}) => {
  const { data, ...restState } = useQuery<
    AxiosResponse<{
      forms: FormType[];
      userMap: ArticleUserMapType;
    }>,
    Error
  >({
    queryKey: formsKeys.getForms({
      companyId,
      filters,
    }),
    queryFn: () =>
      FormsAPI.getForms({
        companyId,
        filters,
      }),
    staleTime: 60 * 1000,
    enabled: shouldFetch && !!companyId,
  });
  const { forms, userMap } = useMemo(
    () => ({
      forms: Array.isArray(data?.data?.forms) ? data?.data?.forms || [] : [],
      userMap: data?.data.userMap,
    }),
    [data],
  );
  const configMappedForms: ConfigMappedFormType[] = useMemo(() => {
    const mappedForms =
      forms?.map((form) => ({
        id: form?.id,
        articles: form?.articles,
        articleIds: form?.articleIds,
        configName: form?.formName,
        configType: form?.formType,
        configCategory: form?.formCategory,
        customFields: form?.customFields,
        description: form?.formCategoryDescription,
        entries: form?.questions,
        title: form?.title,
        companyId: form?.companyId,
        projectId: form?.projectId,
        projectName: form?.project?.name,
        configId: form?.configId,
        updatedAt: form?.updatedAt,
        updatedBy: form?.updatedBy,
        createdAt: form?.createdAt,
        createdBy: form?.createdBy,
        updatedByDisplayName: userMap?.[form?.updatedBy]?.[0]?.['displayName'],
        status: form?.status,
        assignedTo: form?.assignedTo,
        assignedToUserDisplayName: userMap?.[form?.assignedTo]?.[0]?.['displayName'],
        partnerId: form?.contactId,
        statis: form.status,
      })) || [];
    return _.orderBy(mappedForms, ['createdAt'], ['asc']);
  }, [forms, userMap]);

  const updateFormsQueryCache = ({
    formDetails,
    formId,
  }: {
    formDetails?: { form: FormType; userMap: ArticleUserMapType };
    formId?: string;
  }) => {
    const queryStatus = queryClient.getQueryState(formsKeys.getForms({ companyId, filters }));
    if (queryStatus?.status && queryStatus.status !== 'idle') {
      queryClient.setQueryData<
        AxiosResponse<{ forms: FormType[]; userMap: ArticleUserMapType }> | undefined
      >(formsKeys.getForms({ companyId, filters }), (old) => {
        if (old) {
          const existingForms = old?.data?.forms || [];
          const formExists = existingForms.some((form) => form.id === formDetails?.form?.id);
          const updatedUserMaps = { ...old.data.userMap, ...formDetails?.userMap };

          if (formExists) {
            const updatedForms = existingForms.map((form) =>
              form.id === formDetails?.form.id ? { ...form, ...formDetails.form } : form,
            );
            return { ...old, data: { forms: updatedForms, userMap: updatedUserMaps } };
          } else if (!formId) {
            const newForms = formDetails?.form
              ? [formDetails?.form, ...existingForms]
              : existingForms;
            return {
              ...old,
              data: { forms: newForms.filter(Boolean), userMap: updatedUserMaps },
            };
          } else {
            return {
              ...old,
              data: {
                forms: existingForms.filter((form) => form.id !== formId),
                userMap: updatedUserMaps,
              },
            };
          }
        }
        return old;
      });
    }
  };

  return { forms, configMappedForms, userMap, updateFormsQueryCache, ...restState };
};

export const useFormDetailsQuery = ({
  formId = '',
  shouldFetch = true,
}: {
  formId?: string;
  shouldFetch?: boolean;
}) => {
  const { data, isLoading } = useQuery<AxiosResponse<any>, Error>(
    [JAVA_SERVICE_ENDPOINTS.GET_FORM_DETAILS, formId],
    () => FormsAPI.getFormDetails(formId),
    {
      staleTime: 60 * 1000,
      enabled: !!formId && shouldFetch,
    },
  );

  let formData;
  let userMapData;
  if (data?.data?.form) {
    formData = data?.data?.form;
    userMapData = data?.data?.userMap;
  }
  const formDetails = useMemo(
    () =>
      formData
        ? {
            id: formData?.id,
            articles: formData?.articles,
            articleIds: formData?.articleIds,
            configName: formData?.formName,
            configType: formData?.formType,
            configCategory: formData?.formCategory,
            description: formData?.formCategoryDescription,
            entries: formData?.questions,
            sortFlag: formData?.sortFlag,
            title: formData?.title,
            companyId: formData?.companyId,
            projectId: formData?.projectId,
            projectName: formData?.project?.name,
            configId: formData?.configId,
            updatedAt: formData?.updatedAt,
            updatedBy: formData?.updatedBy,
            createdAt: formData?.createdAt,
            createdBy: formData?.createdBy,
            updatedByDisplayName: userMapData[formData?.updatedBy]?.[0]?.['displayName'],
            status: formData?.status,
            assignedTo: formData?.assignedTo,
            assignedToUserDisplayName: userMapData[formData?.assignedTo]?.[0]?.['displayName'],
            partnerId: formData?.contactId,
          }
        : {},
    [data],
  );

  const articles: ArticleType[] =
    (formDetails?.articles &&
      _.orderBy(
        formDetails?.articles.filter((article) => !article?.isDraft),
        ['createdAt'],
        ['desc'],
      )) ||
    [];

  const updateFormDetailsCache = ({
    action,
    article = '',
    formDetails = '',
  }: {
    action: 'add' | 'remove' | 'update';
    article?: any;
    formDetails?: any;
  }) => {
    const formId = formDetails ? formDetails?.form?.id : article?.['formIds']?.[0];
    const queryStatus = queryClient.getQueryState([
      JAVA_SERVICE_ENDPOINTS.GET_FORM_DETAILS,
      formId,
    ]);
    if (queryStatus?.status && queryStatus.status !== 'idle') {
      queryClient.setQueriesData<AxiosResponse<any> | undefined>(
        [JAVA_SERVICE_ENDPOINTS.GET_FORM_DETAILS, formId],
        (old) => {
          if (old) {
            let updatedFormDetails: any = { ...(old?.data?.form || {}) };
            let updatedUserMap = { ...(old?.data?.userMap || {}) };

            if (action === 'remove' && article) {
              let articleId = article?.id;
              updatedFormDetails['articles'] = updatedFormDetails?.articles.filter(
                (article) => article.id !== articleId,
              );
              updatedFormDetails['articleIds'] = updatedFormDetails?.articleIds.filter(
                (id: string) => id !== articleId,
              );
            }

            // It will handle both add & update of articles
            if (action === 'update') {
              if (formDetails) {
                updatedFormDetails = {
                  ...updatedFormDetails,
                  ...formDetails?.form,
                  articles: [...(updatedFormDetails?.articles || [])],
                  articleIds: [...(updatedFormDetails?.articleIds || [])],
                };
                updatedUserMap = {
                  ...updatedUserMap,
                  ...(formDetails?.userMap && formDetails.userMap),
                };
              }

              if (article) {
                let index = updatedFormDetails?.articles?.findIndex(
                  (item) => item.id === article.id,
                );
                if (index > -1) {
                  updatedFormDetails['articles'][index] = { ...article };
                } else {
                  updatedFormDetails['articles'] = [
                    ...(updatedFormDetails?.articles || []),
                    article,
                  ];
                  updatedFormDetails['articleIds'] = [
                    ...(updatedFormDetails?.articleIds || []),
                    article?.id,
                  ];
                }
              }
            }

            return {
              ...old,
              data: {
                ...old.data,
                form: { ...updatedFormDetails },
                userMap: { ...updatedUserMap },
              },
            };
          }
          return old;
        },
      );
    }
  };
  return {
    formDetails,
    isLoading,
    articles,
    updateFormDetailsCache,
    userMap: userMapData,
  };
};

export const useAssignedFormsQuery = ({
  assignedTo,
  companyId,
  createdBy,
  formCategory,
  shouldFetch = true,
  status,
}: {
  assignedTo?: string;
  companyId: string;
  createdBy?: string;
  formCategory?: FORMS_CONFIG_CATEGORY_TYPES;
  shouldFetch?: boolean;
  status: string;
}) => {
  const { data, ...rest } = useQuery<AxiosResponse<any>, Error>(
    [JAVA_SERVICE_ENDPOINTS.GET_FORMS, companyId, createdBy, assignedTo, status, formCategory],
    () => FormsAPI.getAssignedForms({ companyId, createdBy, assignedTo, status, formCategory }),
    {
      staleTime: 60 * 1000,
      enabled: !!companyId && !!(createdBy || assignedTo) && !!status && shouldFetch,
    },
  );

  const formsData = useMemo(() => data?.data, [data]);

  const assignedForms = useMemo(() => _.groupBy(data?.data?.forms || [], 'formCategory'), [data]);

  const updateAssignedFormsCache = ({
    form,
    action,
  }: {
    form: any;
    action: 'add' | 'delete' | 'update';
  }) => {
    const queryStatus = queryClient.getQueryState([
      JAVA_SERVICE_ENDPOINTS.GET_FORMS,
      companyId,
      createdBy,
      assignedTo,
      status,
      formCategory,
    ]);

    if (queryStatus?.status && queryStatus.status !== 'idle') {
      queryClient.setQueryData<AxiosResponse<any> | undefined>(
        [JAVA_SERVICE_ENDPOINTS.GET_FORMS, companyId, createdBy, assignedTo, status, formCategory],
        (old) => {
          if (old) {
            let updatedForms = [...(old?.data?.forms || [])];

            if (action === 'add') {
              updatedForms = [form, ...updatedForms];
            }

            if (action === 'delete') {
              updatedForms = updatedForms.filter((_form) => _form?.id !== form?.id);
            }

            if (action === 'update') {
              let index = updatedForms?.findIndex((_form) => _form.id === form.id);
              if (index > -1) {
                updatedForms[index] = { ...updatedForms[index], ...form };
              } else {
                updatedForms = [form, ...updatedForms];
              }
            }

            return {
              ...old,
              data: {
                ...old.data,
                forms: [...updatedForms],
              },
            };
          }

          return old;
        },
      );
    }
  };

  return {
    assignedProgresscards: assignedForms?.[FORMS_CONFIG_CATEGORY_TYPES.PROJECT_PROGRESS_CARD] || [],
    assignedScorecards: [
      ...(assignedForms?.[FORMS_CONFIG_CATEGORY_TYPES.SCORECARD] || []),
      ...(assignedForms?.[FORMS_CONFIG_CATEGORY_TYPES.BUILDWISE_SCORECARD] || []),
    ],
    requestedPosts: assignedForms?.[FORMS_CONFIG_CATEGORY_TYPES.REQUEST_A_POST_CARD] || [],
    data: formsData,
    ...rest,
    updateAssignedFormsCache,
  };
};
